import { HTMLAttributes, ReactNode, useLayoutEffect, useRef } from 'react'
import { cn } from '@/utils/cn'

interface CollapsibleRegionProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
    children: ReactNode
    contentClassName?: string
    contentOverflowVisible?: boolean
    duration?: 200 | 300
    open: boolean
}

const DURATION_CLASSES = {
    200: 'duration-200',
    300: 'duration-300',
} as const

/** Shared content-height disclosure. Centralizes motion, reduced-motion behavior, and removal of closed
 * content from pointer and keyboard interaction while allowing callers to retain their own trigger UI. */
export const CollapsibleRegion = ({
    children,
    className,
    contentClassName,
    contentOverflowVisible = false,
    duration = 200,
    open,
    ...props
}: CollapsibleRegionProps) => {
    const regionRef = useRef<HTMLDivElement>(null)

    useLayoutEffect(() => {
        if (regionRef.current) regionRef.current.inert = !open
    }, [open])

    return (
        <div
            {...props}
            ref={regionRef}
            aria-hidden={!open}
            className={cn(
                'grid transition-[grid-template-rows] ease-out motion-reduce:transition-none',
                DURATION_CLASSES[duration],
                open ? 'grid-rows-[1fr]' : 'pointer-events-none grid-rows-[0fr]',
                contentOverflowVisible ? 'overflow-visible' : 'overflow-hidden',
                className
            )}
        >
            <div
                className={cn(
                    'min-h-0 transition-opacity duration-200 ease-out motion-reduce:transition-none',
                    open ? 'opacity-100' : 'opacity-0',
                    contentOverflowVisible ? 'overflow-visible' : 'overflow-hidden',
                    contentClassName
                )}
            >
                {children}
            </div>
        </div>
    )
}
